import { DateTypeFilter, Workspace } from '@gripp/shared-logic';
import {
  CardButton,
  colors as CropCircleColors,
  DropDownView,
  LoadingIndicator
} from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { StyleSheet, View } from 'react-native';
import { DataExportModal } from './dataExportModal';
import { useExportHook } from './types';

type ExportButtonProps = {
  useExport: useExportHook;
  fileName: string;
  workspace: Workspace;
  label?: string;
  // When set, scopes the export to a single asset (per-asset Activity Export).
  assetId?: string;
  // When true, the export dropdown shows the "Add all asset info" toggle
  // (asset export only).
  showAssetInfoToggle?: boolean;
};

export const ExportButton = (props: ExportButtonProps) => {
  const csvRef = useRef<any>(null);
  const buttonRef = useRef<View>(null);
  const [download, toggleDownload] = useState(false);
  const [showDataExportModal, setShowDataExportModal] = useState(false);
  const [dateFilter, setDateFilter] = useState<{
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
    addAllAssetInfo?: boolean;
  }>({
    dateType: DateTypeFilter.ALL,
  });

  const onDataReady = () => {
    if (download) doDownload();
  };

  const { headers, rows, isLoading } = props.useExport({
    download: download,
    workspace: props.workspace,
    onDataReady: onDataReady,
    dateFilter: dateFilter,
    assetId: props.assetId,
    addAllAssetInfo: dateFilter.addAllAssetInfo,
  });

  const doDownload = () => {
    setTimeout(() => {
      csvRef.current?.link?.click();
      toggleDownload(false);
      // Reset the date filter after download to remove the filter icon
      setDateFilter({
        dateType: DateTypeFilter.ALL,
      });
    }, 50);
  };

  const onDownload = () => {
    // Always show data export modal first
    setShowDataExportModal(true);
  };

  const onDataExportDownload = (filter: {
    dateType: DateTypeFilter;
    startDate?: string;
    endDate?: string;
    addAllAssetInfo?: boolean;
  }) => {
    setDateFilter(filter);
    // Trigger the download with the selected date filter
    toggleDownload(true);
  };

  return (
    <>
      <View 
        ref={buttonRef}
      >
          {
            isLoading ? (
              <View style={styles.loadingIndicatorContainer}>
                <LoadingIndicator backgroundColor={CropCircleColors.slate[100]} size={12} />
              </View>
            ) : (
              <CardButton
                text={props?.label ?? props.fileName}
                color="white"
                leftIcon="Document B"
                rightIcon="Arrow Down"
                showAvatars={false}
                onPress={onDownload}
                bordered={false}
              />
            )
          }
      </View>
      <CSVLink
        data={rows}
        headers={headers}
        filename={
          props.workspace
            ? `${props.workspace.name}-${props.fileName.toLowerCase()}.csv`
            : `${props.fileName.toLowerCase()}.csv`
        }
        ref={csvRef}
      />
      <DropDownView
        isVisible={showDataExportModal}
        onDismiss={() => setShowDataExportModal(false)}
        anchorRef={buttonRef as any}
        width={450}
        height={650}
        maxHeight={650}
      >
        <DataExportModal
          visible={showDataExportModal}
          onDismiss={() => setShowDataExportModal(false)}
          onDownload={onDataExportDownload}
          initialFilter={dateFilter}
          showAssetInfoToggle={props.showAssetInfoToggle}
        />
      </DropDownView>
    </>
  );
};

const styles = StyleSheet.create({
  loadingIndicatorContainer: {
    width: '100%',
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
  },
});